import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Typography,
  MenuItem,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Checkbox,
  Divider,
  Collapse,
  CircularProgress,
  IconButton,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupIcon from '@mui/icons-material/Group';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import StopIcon from '@mui/icons-material/Stop';

const validationSchema = Yup.object().shape({
  location: Yup.string().required('Pickup location is required'),
  dropoff: Yup.string().required('Drop-off location is required'),
  dateTime: Yup.date()
    .required('Pickup date is required')
    .min(new Date(new Date().setHours(0, 0, 0, 0)), 'Pickup date cannot be in the past')
    .test(
      'is-future-datetime',
      'Pickup time cannot be in the past for the selected date',
      function (value) {
        const { time } = this.parent;
        if (!value || !time) {
          return true;
        }

        try {
          const datePart = new Date(value);
          const [hours, minutes] = time.split(':').map(Number);

          const combinedDateTime = new Date(datePart);
          combinedDateTime.setHours(hours, minutes, 0, 0);

          const now = new Date();

          return combinedDateTime >= now;
        } catch (e) {
          console.error("Error validating date/time:", e);
          return false;
        }
      }
    ),
  time: Yup.string().required('Pickup time is required'),
  passengers: Yup.number()
    .required('Number of passengers is required')
    .min(1, 'Minimum 1 passenger')
    .max(5, 'Maximum 5 passengers'),
  duration: Yup.string().required('Duration is required'),
  hasReturnJourney: Yup.boolean(),
  discountCode: Yup.string().notRequired(),
  // Stops validation
  stops: Yup.array().of(
    Yup.object().shape({
      location: Yup.string().required('Stop location is required'),
    })
  ).max(3, 'Maximum 3 stops allowed'),
  // Conditional validation for return journey fields
  returnLocation: Yup.string().when('hasReturnJourney', {
    is: true,
    then: (schema) => schema.required('Return pickup location is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  returnDropoff: Yup.string().when('hasReturnJourney', {
    is: true,
    then: (schema) => schema.required('Return drop-off location is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  returnDateTime: Yup.date().when('hasReturnJourney', {
    is: true,
    then: (schema) => schema
      .required('Return date is required')
      .min(new Date(new Date().setHours(0, 0, 0, 0)), 'Return date cannot be in the past'),
    otherwise: (schema) => schema.notRequired(),
  }),
  returnTime: Yup.string().when('hasReturnJourney', {
    is: true,
    then: (schema) => schema
      .required('Return time is required')
      .test('return-time-logic', 'Return time must be at least 1 hour after outbound time for same-day journeys', function(value) {
        const { dateTime, time, returnDateTime } = this.parent;
        
        if (!value || !dateTime || !time || !returnDateTime) return true;
        
        // Check if it's the same day
        const outboundDate = new Date(dateTime);
        const returnDate = new Date(returnDateTime);
        const isSameDay = outboundDate.toDateString() === returnDate.toDateString();
        
        if (!isSameDay) return true; // Different days, no time restriction needed
        
        // For same day, ensure return time is at least 1 hour after outbound time
        const [outboundHours, outboundMinutes] = time.split(':').map(Number);
        const [returnHours, returnMinutes] = value.split(':').map(Number);
        
        const outboundTotalMinutes = outboundHours * 60 + outboundMinutes;
        const returnTotalMinutes = returnHours * 60 + returnMinutes;
        
        return returnTotalMinutes >= outboundTotalMinutes + 60; // At least 1 hour difference
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const formatPlaceAddress = (place) => {
  if (!place) return '';
  
  const name = place.name || '';
  const formattedAddress = place.formatted_address || '';
  
  // If the name is already part of the address, just return the formatted address
  if (formattedAddress.includes(name)) {
    return formattedAddress;
  }
  
  // For airports, try to extract the code
  const airportCodeMatch = name.match(/\(([A-Z]{3})\)/);
  if (airportCodeMatch) {
    return `${name} ${formattedAddress}`;
  }
  
  // For other places, combine name and address if they're different
  if (name && formattedAddress && name !== formattedAddress) {
    return `${name}, ${formattedAddress}`;
  }
  
  return formattedAddress;
};

const PickupDetails = ({ data, onNext }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const pickupInputRef = useRef(null);
  const dropoffInputRef = useRef(null);
  const returnPickupInputRef = useRef(null);
  const returnDropoffInputRef = useRef(null);
  const pickupAutocompleteRef = useRef(null);
  const dropoffAutocompleteRef = useRef(null);
  const returnPickupAutocompleteRef = useRef(null);
  const returnDropoffAutocompleteRef = useRef(null);
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pickupCoords, setPickupCoords] = useState(data?.locationCoords || null);
  const [dropoffCoords, setDropoffCoords] = useState(data?.dropoffCoords || null);
  const [returnPickupCoords, setReturnPickupCoords] = useState(data?.returnLocationCoords || null);
  const [returnDropoffCoords, setReturnDropoffCoords] = useState(data?.returnDropoffCoords || null);
  const [stopCoords, setStopCoords] = useState(data?.stopCoords || []);
  const [isValidatingJourney, setIsValidatingJourney] = useState(false);
  const [journeyValidationError, setJourneyValidationError] = useState(null);

  // Helper function to format minutes into hours and minutes
  const formatMinutes = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    
    if (hours === 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else if (minutes === 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
  };

  // Function to validate total journey time with stops
  const validateJourneyWithStops = useCallback(async (pickupCoords, dropoffCoords, stops, selectedDuration) => {
    if (!pickupCoords || !dropoffCoords || !selectedDuration) return true;
    
    setIsValidatingJourney(true);
    setJourneyValidationError(null);
    
    try {
      // Calculate client journey time including stops
      const response = await fetch('/api/calculate-client-journey-time-with-stops', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pickupLocation: { coordinates: pickupCoords },
          dropoffLocation: { coordinates: dropoffCoords },
          stops: stops.map(stop => ({
            coordinates: stop.coordinates,
            duration: 15 // Fixed 15-minute duration for all stops
          }))
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to calculate client journey time with stops');
      }
      
      const metrics = await response.json();
      const journeyTimeMinutes = metrics.totalClientJourneyTimeMinutes;
      const selectedMinutes = parseInt(selectedDuration, 10) * 60;
      
      if (journeyTimeMinutes  > (selectedMinutes + 10 )) {
        const recommendedHours = Math.ceil(journeyTimeMinutes / 60);
        const selectedHours = parseInt(selectedDuration, 10);
        
        // Format journey time in hours and minutes
        const journeyTimeFormatted = formatMinutes(journeyTimeMinutes);
        
        const errorMessage = `Your journey including stops will take ${journeyTimeFormatted}, but you've only booked ${selectedHours} hour${selectedHours > 1 ? 's' : ''} (${selectedMinutes} minutes). Please book at least ${recommendedHours} hour${recommendedHours > 1 ? 's' : ''} for this journey.`;
        setJourneyValidationError(errorMessage);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error validating journey with stops:', error);
      // Don't block booking if validation fails - log error but continue
      return true;
    } finally {
      setIsValidatingJourney(false);
    }
  }, []);

  // Function to validate basic journey time without stops
  const validateBasicJourney = useCallback(async (pickupCoords, dropoffCoords, selectedDuration) => {
    if (!pickupCoords || !dropoffCoords || !selectedDuration) return true;
    
    setIsValidatingJourney(true);
    setJourneyValidationError(null);
    
    try {
      // Calculate client journey time only
      const response = await fetch('/api/calculate-client-journey-time', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pickupLocation: { coordinates: pickupCoords },
          dropoffLocation: { coordinates: dropoffCoords }
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to calculate client journey time');
      }
      
      const metrics = await response.json();
      const journeyTimeMinutes = metrics.clientJourneyTimeMinutes;
      const selectedMinutes = parseInt(selectedDuration, 10) * 60;
      
      if (journeyTimeMinutes > (selectedMinutes + 10)) {
        const recommendedHours = Math.ceil(journeyTimeMinutes / 60);
        const selectedHours = parseInt(selectedDuration, 10);
        
        // Format journey time in hours and minutes
        const journeyTimeFormatted = formatMinutes(journeyTimeMinutes);
        
        const errorMessage = `Your journey will take ${journeyTimeFormatted}, but you've only booked ${selectedHours} hour${selectedHours > 1 ? 's' : ''} (${selectedMinutes} minutes). Please book at least ${recommendedHours} hour${recommendedHours > 1 ? 's' : ''} for this journey.`;
        setJourneyValidationError(errorMessage);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error validating basic journey:', error);
      // Don't block booking if validation fails - log error but continue
      return true;
    } finally {
      setIsValidatingJourney(false);
    }
  }, []);

  // Generate time slots every 15 minutes, marking past times for today as disabled
  const generateTimeSlots = useCallback((selectedDate) => {
    console.log('[generateTimeSlots] Selected Date:', selectedDate);
    const slots = [];
    const now = new Date();
    
    // Floor current time to the next 15-minute interval for comparison (or current if exact)
    const minutesNow = now.getMinutes();
    const nextIntervalMinute = Math.ceil((minutesNow + 1) / 15) * 15; // +1 to ensure current interval is allowed
    const compareTime = new Date(now);
    // Handle hour rollover if needed
    if (nextIntervalMinute >= 60) {
      compareTime.setHours(now.getHours() + 1, 0, 0, 0);
    } else {
      compareTime.setMinutes(nextIntervalMinute, 0, 0); 
    }

    // Check if the selected date is today (ignoring time part)
    const isToday = selectedDate &&
                    selectedDate.getFullYear() === now.getFullYear() &&
                    selectedDate.getMonth() === now.getMonth() &&
                    selectedDate.getDate() === now.getDate();
    
    console.log('[generateTimeSlots] Is Today?:', isToday);
    console.log('[generateTimeSlots] Compare Time (Ceiled):', compareTime);

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        // Create a date object for the slot time *on the selected date*
        const slotDateTime = new Date(selectedDate);
        slotDateTime.setHours(hour, minute, 0, 0);

        // Determine if the slot should be disabled
        // Disabled if it's today AND the slot time is strictly before the floored current time
        const isDisabled = isToday && slotDateTime < compareTime; 
        
        const hourStr = hour.toString().padStart(2, '0');
        const minStr = minute.toString().padStart(2, '0');
        const timeStr = `${hourStr}:${minStr}`;
        // Display in 12-hour format with AM/PM
        const displayHour = hour % 12 || 12;
        const ampm = hour < 12 ? 'AM' : 'PM';
        const displayStr = `${displayHour}:${minStr} ${ampm}`;
        
        slots.push({ value: timeStr, label: displayStr, isDisabled: isDisabled });
      }
    }
    console.log('[generateTimeSlots] Generated Slots:', slots.length);
    return slots;
  }, []);

  const formik = useFormik({
    initialValues: {
      location: data?.location || '',
      dropoff: data?.dropoff || '',
      dateTime: data?.dateTime ? new Date(Math.max(new Date(data.dateTime), new Date().setHours(0,0,0,0))) : new Date(),
      time: (() => {
        const initialDate = data?.dateTime ? new Date(data.dateTime) : new Date();
        let initialTime = '09:00';
        if (data?.dateTime) {
          initialTime = new Date(data.dateTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
        }
        const availableSlots = generateTimeSlots(initialDate);
        const isValidInitialTime = availableSlots.some(slot => slot.value === initialTime);
        return isValidInitialTime ? initialTime : (availableSlots[0]?.value || '00:00');
      })(),
      passengers: data?.passengers || '1',
      duration: data?.duration || '1',
      discountCode: data?.discountCode || '',
      // Stops
      stops: data?.stops || [],
      // Return journey fields
      hasReturnJourney: data?.hasReturnJourney || false,
      returnLocation: data?.returnLocation || '',
      returnDropoff: data?.returnDropoff || '',
      returnDateTime: data?.returnDateTime ? new Date(data.returnDateTime) : new Date(),
      returnTime: data?.returnTime || '18:00',
    },
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      if (!pickupCoords || !dropoffCoords) {
        setError('Please select valid pickup and drop-off locations');
        return;
      }

      if (values.hasReturnJourney && (!returnPickupCoords || !returnDropoffCoords)) {
        setError('Please select valid return pickup and drop-off locations');
        return;
      }

      // Validate journey time (with or without stops)
      if (values.stops && values.stops.length > 0) {
        // Validate stops have coordinates
        const hasInvalidStops = values.stops.some((stop, index) => !stopCoords[index]);
        if (hasInvalidStops) {
          setError('Please select valid locations for all stops');
          return;
        }

        // Validate journey time with stops
        const stopsWithCoords = values.stops.map((stop, index) => ({
          ...stop,
          duration: 15, // Fixed 15-minute duration for all stops
          coordinates: stopCoords[index]
        }));
        
        const isValidJourney = await validateJourneyWithStops(
          pickupCoords, 
          dropoffCoords, 
          stopsWithCoords, 
          values.duration
        );
        
        if (!isValidJourney) {
          setError(journeyValidationError);
          return;
        }
      } else {
        // Validate basic journey without stops
        const isValidBasicJourney = await validateBasicJourney(
          pickupCoords,
          dropoffCoords,
          values.duration
        );
        
        if (!isValidBasicJourney) {
          setError(journeyValidationError);
          return;
        }
      }

      const datePart = values.dateTime;
      const [hours, minutes] = values.time.split(':').map(Number);
      
      const combinedDateTime = new Date(datePart);
      combinedDateTime.setHours(hours, minutes, 0, 0);

      const finalDateTimeISO = combinedDateTime.toISOString();

      let returnDateTimeISO = null;
      if (values.hasReturnJourney) {
        const returnDatePart = values.returnDateTime;
        const [returnHours, returnMinutes] = values.returnTime.split(':').map(Number);
        
        const combinedReturnDateTime = new Date(returnDatePart);
        combinedReturnDateTime.setHours(returnHours, returnMinutes, 0, 0);
        
        returnDateTimeISO = combinedReturnDateTime.toISOString();
      }

      const finalData = {
        ...values,
        dateTime: finalDateTimeISO,
        locationCoords: pickupCoords,
        dropoffCoords: dropoffCoords,
        returnDateTime: returnDateTimeISO,
        returnLocationCoords: returnPickupCoords,
        returnDropoffCoords: returnDropoffCoords,
        stopCoords: stopCoords,
      };

      console.log('Submitting booking with stops and return journey data:', finalData);
      onNext({
        pickup: finalData
      });
    },
  });

  // Handler for return journey checkbox
  const handleReturnJourneyChange = (event) => {
    const isChecked = event.target.checked;
    formik.setFieldValue('hasReturnJourney', isChecked);
    
    if (isChecked) {
      // Auto-populate return journey with reverse of outbound journey
      formik.setFieldValue('returnLocation', formik.values.dropoff);
      formik.setFieldValue('returnDropoff', formik.values.location);
      setReturnPickupCoords(dropoffCoords);
      setReturnDropoffCoords(pickupCoords);
      
      // Set the actual input values for autocomplete fields
      setTimeout(() => {
        if (returnPickupInputRef.current) {
          returnPickupInputRef.current.value = formik.values.dropoff;
        }
        if (returnDropoffInputRef.current) {
          returnDropoffInputRef.current.value = formik.values.location;
        }
      }, 100);
    } else {
      // Clear return journey data
      formik.setFieldValue('returnLocation', '');
      formik.setFieldValue('returnDropoff', '');
      formik.setFieldValue('returnDateTime', new Date());
      formik.setFieldValue('returnTime', '18:00');
      setReturnPickupCoords(null);
      setReturnDropoffCoords(null);
      
      // Clear the actual input values for autocomplete fields
      setTimeout(() => {
        if (returnPickupInputRef.current) {
          returnPickupInputRef.current.value = '';
        }
        if (returnDropoffInputRef.current) {
          returnDropoffInputRef.current.value = '';
        }
      }, 100);
    }
  };

  const handlePickupSelect = useCallback((place) => {
    if (!place.geometry) {
      setError('Please select a location from the dropdown');
      return;
    }

    const formattedAddress = formatPlaceAddress(place);
    setPickupCoords({
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng()
    });
    formik.setFieldValue('location', formattedAddress);
    setError(null);
  }, [formik]);

  const handleDropoffSelect = useCallback((place) => {
    if (!place.geometry) {
      setError('Please select a location from the dropdown');
      return;
    }

    const formattedAddress = formatPlaceAddress(place);
    setDropoffCoords({
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng()
    });
    formik.setFieldValue('dropoff', formattedAddress);
    setError(null);
  }, [formik]);

  // Add new handlers for return journey locations
  const handleReturnPickupSelect = useCallback((place) => {
    if (!place.geometry) {
      setError('Please select a location from the dropdown');
      return;
    }

    const formattedAddress = formatPlaceAddress(place);
    setReturnPickupCoords({
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng()
    });
    formik.setFieldValue('returnLocation', formattedAddress);
    setError(null);
  }, [formik]);

  const handleReturnDropoffSelect = useCallback((place) => {
    if (!place.geometry) {
      setError('Please select a location from the dropdown');
      return;
    }

    const formattedAddress = formatPlaceAddress(place);
    setReturnDropoffCoords({
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng()
    });
    formik.setFieldValue('returnDropoff', formattedAddress);
    setError(null);
  }, [formik]);

  // Handle stop location selection
  const handleStopSelect = useCallback((stopIndex, place) => {
    if (!place.geometry) {
      setError('Please select a location from the dropdown');
      return;
    }

    const address = formatPlaceAddress(place);
    const coords = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng()
    };
    
    // Update stop location in formik
    const currentStops = [...formik.values.stops];
    currentStops[stopIndex] = { ...currentStops[stopIndex], location: address };
    formik.setFieldValue('stops', currentStops);
    
    // Update stop coordinates
    const newStopCoords = [...stopCoords];
    newStopCoords[stopIndex] = coords;
    setStopCoords(newStopCoords);
    setError(null);
  }, [formik, stopCoords]);

  useEffect(() => {
    if (!pickupInputRef.current || !dropoffInputRef.current || !window.google?.maps?.places) {
      setError('Location search is currently unavailable');
      return;
    }

    // Setup main journey autocomplete
    pickupAutocompleteRef.current = new window.google.maps.places.Autocomplete(pickupInputRef.current, {
      componentRestrictions: { country: 'gb' },
      fields: ['formatted_address', 'geometry', 'name', 'place_id', 'types'],
      types: ['geocode', 'establishment'],
    });

    dropoffAutocompleteRef.current = new window.google.maps.places.Autocomplete(dropoffInputRef.current, {
      componentRestrictions: { country: 'gb' },
      fields: ['formatted_address', 'geometry', 'name', 'place_id', 'types'],
      types: ['geocode', 'establishment'],
    });

    // Setup return journey autocomplete (if elements exist)
    let returnPickupListener = null;
    let returnDropoffListener = null;

    if (returnPickupInputRef.current) {
      returnPickupAutocompleteRef.current = new window.google.maps.places.Autocomplete(returnPickupInputRef.current, {
        componentRestrictions: { country: 'gb' },
        fields: ['formatted_address', 'geometry', 'name', 'place_id', 'types'],
        types: ['geocode', 'establishment'],
      });

      returnPickupListener = returnPickupAutocompleteRef.current.addListener('place_changed', () => {
        const place = returnPickupAutocompleteRef.current.getPlace();
        handleReturnPickupSelect(place);
      });
    }

    if (returnDropoffInputRef.current) {
      returnDropoffAutocompleteRef.current = new window.google.maps.places.Autocomplete(returnDropoffInputRef.current, {
        componentRestrictions: { country: 'gb' },
        fields: ['formatted_address', 'geometry', 'name', 'place_id', 'types'],
        types: ['geocode', 'establishment'],
      });

      returnDropoffListener = returnDropoffAutocompleteRef.current.addListener('place_changed', () => {
        const place = returnDropoffAutocompleteRef.current.getPlace();
        handleReturnDropoffSelect(place);
      });
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
    };

    // Add event listeners for main journey fields
    pickupInputRef.current.addEventListener('keydown', handleKeyDown);
    dropoffInputRef.current.addEventListener('keydown', handleKeyDown);

    // Add event listeners for return journey fields (if they exist)
    if (returnPickupInputRef.current) {
      returnPickupInputRef.current.addEventListener('keydown', handleKeyDown);
    }
    if (returnDropoffInputRef.current) {
      returnDropoffInputRef.current.addEventListener('keydown', handleKeyDown);
    }

    const pickupListener = pickupAutocompleteRef.current.addListener('place_changed', () => {
      const place = pickupAutocompleteRef.current.getPlace();
      handlePickupSelect(place);
    });

    const dropoffListener = dropoffAutocompleteRef.current.addListener('place_changed', () => {
      const place = dropoffAutocompleteRef.current.getPlace();
      handleDropoffSelect(place);
    });

    const style = document.createElement('style');
    style.textContent = `
      .pac-container {
        border-radius: 8px;
        margin-top: 4px;
        padding: 8px 0;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        z-index: 1500 !important;
      }
      .pac-item {
        padding: 8px 16px;
        cursor: pointer;
        font-size: 14px;
        line-height: 1.5;
        display: flex;
        align-items: center;
      }
      .pac-item:hover {
        background-color: rgba(0, 0, 0, 0.04);
      }
      .pac-item-selected {
        background-color: rgba(0, 0, 0, 0.08);
      }
      .pac-icon {
        margin-right: 12px;
      }
      .pac-item-query {
        font-size: 14px;
        padding-right: 4px;
      }
      .pac-matched {
        font-weight: 600;
      }
      .pac-item span:last-child {
        font-size: 13px;
        color: #666;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const pickup = pickupInputRef.current;
      const dropoff = dropoffInputRef.current;
      const returnPickup = returnPickupInputRef.current;
      const returnDropoff = returnDropoffInputRef.current;
      
      // Remove event listeners for main journey fields
      if (pickup) {
        pickup.removeEventListener('keydown', handleKeyDown);
      }
      if (dropoff) {
        dropoff.removeEventListener('keydown', handleKeyDown);
      }

      // Remove event listeners for return journey fields
      if (returnPickup) {
        returnPickup.removeEventListener('keydown', handleKeyDown);
      }
      if (returnDropoff) {
        returnDropoff.removeEventListener('keydown', handleKeyDown);
      }
      
      // Remove Google Maps event listeners
      if (pickupListener) {
        window.google.maps.event.removeListener(pickupListener);
      }
      if (dropoffListener) {
        window.google.maps.event.removeListener(dropoffListener);
      }
      if (returnPickupListener) {
        window.google.maps.event.removeListener(returnPickupListener);
      }
      if (returnDropoffListener) {
        window.google.maps.event.removeListener(returnDropoffListener);
      }
      
      document.head.removeChild(style);
    };
  }, [handlePickupSelect, handleDropoffSelect, handleReturnPickupSelect, handleReturnDropoffSelect, formik.values.hasReturnJourney]);

  // Re-generate time slots when the date changes
  const timeSlots = useMemo(() => generateTimeSlots(formik.values.dateTime), [formik.values.dateTime, generateTimeSlots]);

  // Effect to reset time if it becomes invalid after date change
  useEffect(() => {
    const selectedTime = formik.values.time;
    const selectedDate = formik.values.dateTime;
    
    console.log('[useEffect] Date changed, checking time validity. Current Time:', selectedTime, 'Slots:', timeSlots.length);

    if (!selectedDate || !selectedTime) return; // Don't run if date/time aren't set

    const isTimeValidForCurrentSlots = timeSlots.some(slot => slot.value === selectedTime);

    // If the currently selected time is no longer in the available slots 
    // (e.g., date changed to today and time is now past)
    if (!isTimeValidForCurrentSlots && timeSlots.length > 0) {
      const firstAvailableSlot = timeSlots[0].value;
      console.log('[useEffect] Selected time is invalid for new date. Resetting time to:', firstAvailableSlot);
      formik.setFieldValue('time', firstAvailableSlot);
    } else if (timeSlots.length === 0 && selectedTime) {
      // Handle case where no slots are available for the selected date
      console.log('[useEffect] No available slots for selected date. Clearing time.');
      formik.setFieldValue('time', ''); // Clear time if no slots
    } else {
      console.log('[useEffect] Selected time is still valid for the current date/slots.');
    }
  // Only re-run when timeSlots array itself changes instance (due to date change)
  // formik is stable, setFieldValue is stable
  }, [timeSlots, formik.values.dateTime, formik.setFieldValue]); // Added dateTime dependency for clarity

  const returnTimeSlots = useMemo(() => generateTimeSlots(formik.values.returnDateTime), [formik.values.returnDateTime, generateTimeSlots]);

  // Effect to set initial values for return journey autocomplete fields
  useEffect(() => {
    if (formik.values.hasReturnJourney && returnPickupInputRef.current && returnDropoffInputRef.current) {
      setTimeout(() => {
        if (formik.values.returnLocation && returnPickupInputRef.current) {
          returnPickupInputRef.current.value = formik.values.returnLocation;
        }
        if (formik.values.returnDropoff && returnDropoffInputRef.current) {
          returnDropoffInputRef.current.value = formik.values.returnDropoff;
        }
      }, 200);
    }
  }, [formik.values.hasReturnJourney, formik.values.returnLocation, formik.values.returnDropoff]);

  // Effect to set up autocomplete for dynamically added stops
  useEffect(() => {
    if (!window.google?.maps?.places) return;

    const setupStopAutocomplete = () => {
      const stopInputs = document.querySelectorAll('input[id^="stop-location-"]');
      
      stopInputs.forEach((input, index) => {
        // Check if autocomplete is already set up for this input
        if (input.dataset.autocompleteSetup) return;
        
        const autocomplete = new window.google.maps.places.Autocomplete(input, {
          componentRestrictions: { country: 'gb' },
          fields: ['formatted_address', 'geometry', 'name', 'place_id', 'types'],
          types: ['geocode', 'establishment'],
        });

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          handleStopSelect(index, place);
        });

        // Add event listener for keydown
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        });

        // Mark as set up
        input.dataset.autocompleteSetup = 'true';
      });
    };

    // Set up autocomplete after a small delay to ensure DOM is ready
    const timeoutId = setTimeout(setupStopAutocomplete, 100);
    return () => clearTimeout(timeoutId);
  }, [formik.values.stops.length, handleStopSelect]);

  // Auto-validate journey duration when locations or duration change
  useEffect(() => {
    if (pickupCoords && dropoffCoords && formik.values.duration) {
      const timeoutId = setTimeout(() => {
        if (formik.values.stops && formik.values.stops.length > 0) {
          // Check if all stops have coordinates
          const hasAllStopCoords = formik.values.stops.every((stop, index) => stopCoords[index]);
          if (hasAllStopCoords) {
            const stopsWithCoords = formik.values.stops.map((stop, index) => ({
              ...stop,
              duration: 15, // Fixed 15-minute duration for all stops
              coordinates: stopCoords[index]
            }));
            validateJourneyWithStops(pickupCoords, dropoffCoords, stopsWithCoords, formik.values.duration);
          }
        } else {
          // Validate basic journey without stops
          validateBasicJourney(pickupCoords, dropoffCoords, formik.values.duration);
        }
      }, 1000); // Debounce API calls
      
      return () => clearTimeout(timeoutId);
    }
  }, [pickupCoords, dropoffCoords, formik.values.duration, formik.values.stops, stopCoords, validateJourneyWithStops, validateBasicJourney]);

  return (
    <Card 
      elevation={isMobile ? 0 : 1}
      sx={{ 
        borderRadius: { xs: 0, sm: 2 },
        border: { xs: 'none', sm: '1px solid rgba(0, 0, 0, 0.12)' },
        width: '100%',
      }}
    >
      <CardContent 
        sx={{ 
          p: { xs: 2, sm: 3 },
          '&:last-child': { pb: { xs: 2, sm: 3 } },
          width: '100%',
        }}
      >
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            mb: { xs: 2, sm: 3 },
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            fontWeight: 600,
            textAlign: { xs: 'center', sm: 'left' },
          }}
        >
          Enter Your Journey Details
        </Typography>

        {error && (
          <Box 
            sx={{ 
              mb: 2, 
              p: 2, 
              bgcolor: 'error.light', 
              borderRadius: 1,
              mx: { xs: -1, sm: 0 },
            }}
          >
            <Typography color="error" variant="body2">{error}</Typography>
          </Box>
        )}

        {journeyValidationError && (
          <Box 
            sx={{ 
              mb: 2, 
              p: 2, 
              bgcolor: 'warning.light', 
              borderRadius: 1,
              mx: { xs: -1, sm: 0 },
            }}
          >
            <Typography color="warning" variant="body2">{journeyValidationError}</Typography>
          </Box>
        )}

        {isValidatingJourney && (
          <Box 
            sx={{ 
              mb: 2, 
              p: 2, 
              bgcolor: 'info.light', 
              borderRadius: 1,
              mx: { xs: -1, sm: 0 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Validating journey duration...</Typography>
            </Box>
          </Box>
        )}

        <form onSubmit={formik.handleSubmit} noValidate>
          <Grid 
            container 
            spacing={{ xs: 2, sm: 3 }}
            sx={{ width: '100%', m: 0 }}
          >
            {/* Pickup and Dropoff Fields - Remove Outbound Journey title */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                inputRef={pickupInputRef}
                id="location"
                name="location"
                label="Pickup Location"
                placeholder="Enter pickup location"
                onFocus={() => {
                  if (pickupInputRef.current) {
                    pickupInputRef.current.select();
                  }
                }}
                error={formik.touched.location && Boolean(formik.errors.location)}
                helperText={formik.touched.location && formik.errors.location}
                InputProps={{
                  startAdornment: <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: 'background.paper',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                inputRef={dropoffInputRef}
                id="dropoff"
                name="dropoff"
                label="Drop-off Location"
                placeholder="Enter drop-off location"
                onFocus={() => {
                  if (dropoffInputRef.current) {
                    dropoffInputRef.current.select();
                  }
                }}
                error={formik.touched.dropoff && Boolean(formik.errors.dropoff)}
                helperText={formik.touched.dropoff && formik.errors.dropoff}
                InputProps={{
                  startAdornment: <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: 'background.paper',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Pickup Date"
                value={formik.values.dateTime}
                onChange={(value) => formik.setFieldValue('dateTime', value)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: formik.touched.dateTime && Boolean(formik.errors.dateTime),
                    helperText: formik.touched.dateTime && formik.errors.dateTime,
                  },
                }}
                minDate={new Date()}
                format="dd/MM/yyyy"
                sx={{
                  '& .MuiInputBase-root': {
                    borderRadius: '8px',
                    bgcolor: 'background.paper',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                id="time"
                name="time"
                label="Pickup Time"
                value={formik.values.time}
                onChange={formik.handleChange}
                error={formik.touched.time && Boolean(formik.errors.time)}
                helperText={formik.touched.time && formik.errors.time}
                InputProps={{
                  startAdornment: <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: 'background.paper',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                  },
                }}
              >
                {timeSlots.map((slot) => (
                  <MenuItem 
                    key={slot.value} 
                    value={slot.value} 
                    disabled={slot.isDisabled}
                    sx={{
                      ...(slot.isDisabled && {
                        color: 'text.disabled',
                        fontStyle: 'italic',
                      })
                    }}
                  >
                    {slot.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                id="passengers"
                name="passengers"
                label="Number of Passengers"
                value={formik.values.passengers}
                onChange={formik.handleChange}
                error={formik.touched.passengers && Boolean(formik.errors.passengers)}
                helperText={formik.touched.passengers && formik.errors.passengers}
                InputProps={{
                  startAdornment: <GroupIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              >
                {[1, 2, 3, 4, 5].map((option) => (
                  <MenuItem key={option} value={option.toString()}>
                    {option} {option === 1 ? 'Passenger' : 'Passengers'}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                id="duration"
                name="duration"
                label="Journey Duration"
                value={formik.values.duration}
                onChange={formik.handleChange}
                error={formik.touched.duration && Boolean(formik.errors.duration)}
                helperText={formik.touched.duration && formik.errors.duration}
                InputProps={{
                  startAdornment: <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              >
                {[...Array(12)].map((_, index) => {
                  const hours = index + 1;
                  return (
                    <MenuItem key={hours} value={hours.toString()}>
                      {hours} {hours === 1 ? 'Hour' : 'Hours'}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>

            {/* Discount Code Field */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="discountCode"
                name="discountCode"
                label="Discount Code (Optional)"
                value={formik.values.discountCode}
                onChange={formik.handleChange}
                placeholder="Enter discount code"
                InputProps={{
                  startAdornment: <LocalOfferIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: 'background.paper',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                  },
                }}
              />
            </Grid>

            {/* Stops Section */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  Add stops ({formik.values.stops?.length || 0}/3)
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    const currentStops = formik.values.stops || [];
                    if (currentStops.length < 3) {
                      formik.setFieldValue('stops', [...currentStops, { location: '' }]);
                      setStopCoords([...stopCoords, null]);
                    }
                  }}
                  disabled={formik.values.stops?.length >= 3}
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.light',
                    },
                    '&:disabled': {
                      color: 'text.disabled',
                    },
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            </Grid>

            {/* Render stops */}
            {formik.values.stops?.map((stop, index) => (
              <Grid item xs={12} key={index}>
                <Collapse in={true} timeout="auto">
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <TextField
                      fullWidth
                      id={`stop-location-${index}`}
                      label={`Stop ${index + 1} Location`}
                      value={stop.location}
                      onChange={(e) => {
                        const newStops = [...formik.values.stops];
                        newStops[index] = { ...newStops[index], location: e.target.value };
                        formik.setFieldValue('stops', newStops);
                      }}
                      onFocus={(e) => e.target.select()}
                      error={formik.touched.stops?.[index]?.location && Boolean(formik.errors.stops?.[index]?.location)}
                      helperText={formik.touched.stops?.[index]?.location && formik.errors.stops?.[index]?.location}
                      InputProps={{
                        startAdornment: <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: 'background.paper',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.12)',
                        },
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        const currentStops = formik.values.stops || [];
                        const newStops = currentStops.filter((_, i) => i !== index);
                        formik.setFieldValue('stops', newStops);
                        
                        const newStopCoords = stopCoords.filter((_, i) => i !== index);
                        setStopCoords(newStopCoords);
                      }}
                      sx={{ 
                        color: 'error.main',
                        mt: 0.5,
                        '&:hover': {
                          bgcolor: 'error.light',
                        }
                      }}
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Box>
                </Collapse>
              </Grid>
            ))}

            {/* Return Journey Toggle */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1.5 }} />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.hasReturnJourney}
                    onChange={handleReturnJourneyChange}
                    name="hasReturnJourney"
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SwapVertIcon sx={{ mr: 1 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Add return journey
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            {/* Return Journey Fields - Only render when needed */}
            {formik.values.hasReturnJourney && (
              <>
                <Grid item xs={12}>
                  <Collapse in={formik.values.hasReturnJourney} timeout="auto" unmountOnExit>
                    <Box sx={{ pt: 1 }}>
                      <TextField
                        fullWidth
                        inputRef={returnPickupInputRef}
                        id="returnLocation"
                        name="returnLocation"
                        label="Return Pickup Location"
                        placeholder="Enter return pickup location"
                        onFocus={() => {
                          if (returnPickupInputRef.current) {
                            returnPickupInputRef.current.select();
                          }
                        }}
                        error={formik.touched.returnLocation && Boolean(formik.errors.returnLocation)}
                        helperText={formik.touched.returnLocation && formik.errors.returnLocation}
                        InputProps={{
                          startAdornment: <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            bgcolor: 'background.paper',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                          },
                        }}
                      />
                    </Box>
                  </Collapse>
                </Grid>

                <Grid item xs={12}>
                  <Collapse in={formik.values.hasReturnJourney} timeout="auto" unmountOnExit>
                    <Box>
                      <TextField
                        fullWidth
                        inputRef={returnDropoffInputRef}
                        id="returnDropoff"
                        name="returnDropoff"
                        label="Return Drop-off Location"
                        placeholder="Enter return drop-off location"
                        onFocus={() => {
                          if (returnDropoffInputRef.current) {
                            returnDropoffInputRef.current.select();
                          }
                        }}
                        error={formik.touched.returnDropoff && Boolean(formik.errors.returnDropoff)}
                        helperText={formik.touched.returnDropoff && formik.errors.returnDropoff}
                        InputProps={{
                          startAdornment: <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            bgcolor: 'background.paper',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                          },
                        }}
                      />
                    </Box>
                  </Collapse>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Collapse in={formik.values.hasReturnJourney} timeout="auto" unmountOnExit>
                    <Box>
                      <DatePicker
                        label="Return Date"
                        value={formik.values.returnDateTime}
                        onChange={(value) => formik.setFieldValue('returnDateTime', value)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: formik.touched.returnDateTime && Boolean(formik.errors.returnDateTime),
                            helperText: formik.touched.returnDateTime && formik.errors.returnDateTime,
                          },
                        }}
                        minDate={formik.values.dateTime || new Date()}
                        format="dd/MM/yyyy"
                        sx={{
                          '& .MuiInputBase-root': {
                            borderRadius: '8px',
                            bgcolor: 'background.paper',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                          },
                        }}
                      />
                    </Box>
                  </Collapse>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Collapse in={formik.values.hasReturnJourney} timeout="auto" unmountOnExit>
                    <Box>
                      <TextField
                        fullWidth
                        select
                        id="returnTime"
                        name="returnTime"
                        label="Return Time"
                        value={formik.values.returnTime}
                        onChange={formik.handleChange}
                        error={formik.touched.returnTime && Boolean(formik.errors.returnTime)}
                        helperText={formik.touched.returnTime && formik.errors.returnTime}
                        InputProps={{
                          startAdornment: <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                              },
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            bgcolor: 'background.paper',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                          },
                        }}
                      >
                        {returnTimeSlots.map((slot) => (
                          <MenuItem 
                            key={slot.value} 
                            value={slot.value}
                            disabled={slot.isDisabled}
                            sx={{
                              ...(slot.isDisabled && {
                                color: 'text.disabled',
                                fontStyle: 'italic',
                              })
                            }}
                          >
                            {slot.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  </Collapse>
                </Grid>
              </>
            )}

            {/* Submit Button - Changed back to just "Next" */}
            <Grid 
              item 
              xs={12} 
              sx={{ 
                mt: formik.values.hasReturnJourney ? { xs: 1, sm: 2 } : { xs: 0.5, sm: 1 },
                position: 'sticky',
                bottom: { xs: 0, sm: 'auto' },
                bgcolor: 'background.paper',
                zIndex: 1,
                py: { xs: 2, sm: 0 },
                mx: { xs: -2, sm: 0 },
                px: { xs: 2, sm: 0 },
                borderTop: { xs: '1px solid rgba(0, 0, 0, 0.12)', sm: 'none' },
                left: 0,
                right: 0,
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  sx={{ 
                    height: 48,
                    bgcolor: 'background.dark',
                    color: 'common.white',
                    borderRadius: '8px',
                    boxShadow: { xs: 'none', sm: 1 },
                    '&:hover': {
                      bgcolor: 'background.dark',
                      opacity: 0.9,
                    },
                  }}
                  disabled={
                    !pickupCoords || 
                    !dropoffCoords || 
                    !formik.isValid || 
                    Boolean(error) ||
                    (formik.values.hasReturnJourney && (!returnPickupCoords || !returnDropoffCoords))
                  }
                >
                  Next
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
};

export default PickupDetails; 